<script setup lang="ts">
import { useTemplateRef } from "vue";
import { useModalStore } from "@/stores/modal";

const props = defineProps<{
  controller: ReturnType<typeof useModal>;
}>();

const { controller } = toRefs(props);
const { isOpen, close } = controller.value;

const modalStore = useModalStore();

// Store the element that had focus before modal opened
const previouslyFocusedElement = ref<HTMLElement | null>(null);

// Watch for modal opening/closing and sync with modalStore
watch(isOpen, (isModalOpen: boolean) => {
  if (isModalOpen) {
    modalStore.openModal();
    // Store the currently focused element
    previouslyFocusedElement.value = document.activeElement as HTMLElement;
    // Focus will be set after the modal is rendered
    nextTick(() => {
      focusFirstFocusableElement();
    });
  } else {
    modalStore.closeModal();
    // Restore focus to the previously focused element
    if (previouslyFocusedElement.value) {
      previouslyFocusedElement.value.focus();
      previouslyFocusedElement.value = null;
    }
  }
});

const { escape: escapeKey } = useMagicKeys();

watch(escapeKey, () => {
  isOpen.value && close();
});

const modalContentElement = useTemplateRef("modalContentElement");
onClickOutside(modalContentElement, () => close());

// Function to get all focusable elements within the modal
const getFocusableElements = (): HTMLElement[] => {
  if (!modalContentElement.value) return [];
  
  const focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]'
  ];
  
  return Array.from(
    modalContentElement.value.querySelectorAll(focusableSelectors.join(', '))
  ) as HTMLElement[];
};

// Function to focus the first focusable element
const focusFirstFocusableElement = () => {
  const focusableElements = getFocusableElements();
  if (focusableElements.length > 0) {
    focusableElements[0].focus();
  }
};

// Function to handle tab key for focus trapping
const handleTabKey = (event: KeyboardEvent) => {
  if (!isOpen.value || !modalContentElement.value) return;
  
  const focusableElements = getFocusableElements();
  if (focusableElements.length === 0) return;
  
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  
  if (event.shiftKey) {
    // Shift + Tab: move backwards
    if (document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    }
  } else {
    // Tab: move forwards
    if (document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }
};
const handleModalClick = (event: MouseEvent) => {
  if (!modalContentElement.value) return;

  const target = (event.target as HTMLElement | null)?.closest(
    'a[href^="#"]'
  ) as HTMLAnchorElement | null;

  if (!target || !modalContentElement.value.contains(target)) return;

  const href = target.getAttribute("href") || "";
  if (!href.startsWith("#") || href === "#") return;

  event.preventDefault();

  const id = decodeURIComponent(href.slice(1));

  let selector = `#${id}`;
  if (typeof CSS !== "undefined" && CSS.escape) {
    selector = `#${CSS.escape(id)}`;
  }

  const section = modalContentElement.value.querySelector<HTMLElement>(
    selector
  );

  if (section) {
    const container = modalContentElement.value;
    const offsetTop = section.offsetTop - container.offsetTop;

    container.scrollTo({
      top: offsetTop,
      behavior: "smooth",
    });
  }
};

// Add keyboard event listener for focus trapping
onMounted(() => {
  document.addEventListener('keydown', handleTabKey);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleTabKey);
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200 transform"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-200 transform"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-show="isOpen"
        class="fixed z-[1000] inset-0 overflow-y-auto bg-black bg-opacity-50"
      >
        <div
          class="flex items-center justify-center min-h-screen lg:my-12text-center"
        >
          <Transition
            enter-active-class="transition ease-out duration-500 transform "
            enter-from-class="opacity-0 translate-y-10 scale-95"
            enter-to-class="opacity-100 translate-y-0 scale-100"
            leave-active-class="ease-in duration-200"
            leave-from-class="opacity-100 translate-y-0 scale-100"
            leave-to-class="opacity-0 translate-y-10 translate-y-0 scale-95"
          >
            <div
              v-if="isOpen"
              id="modal-content"
              ref="modalContentElement"
              class="bg-white rounded-none md:rounded-[20px] text-left overflow-hidden shadow-xl p-4 md:p-8 w-full max-w-[600px] min-h-[100vh] md:min-h-0 max-h-[100vh] md:max-h-[90vh] overflow-y-auto hide-scrollbar relative"
              role="dialog"
              @click="handleModalClick"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <!-- Close button -->
              <button
                @click="close"
                class="absolute top-4 right-4 md:top-6 md:right-6 text-xl bg-transparent text-secondary-400 hover:text-secondary-500"
                aria-label="Close modal"
                type="button"
              >
                <span class="sr-only">Close panel</span>
                <div class="w-6 h-6 icon-close-outline"></div>
              </button>
              
              <slot></slot>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
